import { normalizeLocaleCode } from './i18n';

export interface CartModeIconSettings {
  show_badge?: boolean;
}

export interface CartModeCompactSettings {
  show_price?: boolean;
  show_count?: boolean;
}

export interface CartModeSummarySettings {
  show_title?: boolean;
  show_price?: boolean;
  show_count?: boolean;
  button_bg?: string;
  button_background_color?: string;
  button_color?: string;
  button_text_color?: string;
  button_text_colour?: string;
  text_color?: string;
  /** Per-locale title. Falls back to en, then to i18n default. */
  titles?: Record<string, string>;
  /** Per-locale button text. Falls back to en, then to i18n default. */
  button_texts?: Record<string, string>;
}

export type CartIconType = 'cart' | 'cart3' | 'cart2' | 'cart-plus' | 'bag' | 'handbag' | 'basket' | 'basket2' | 'shop';

/** Localized string: plain or per-locale map (en/de/…). */
export type CartLocalizedString = string | Record<string, string>;

export interface CartPageTrustItem {
  /** Icon key for tiny inline SVG map (`truck`, `shield-check`, …). No icon font. */
  icon?: string;
  label: CartLocalizedString;
}

export interface CartPagePayItem {
  icon?: string;
  label?: CartLocalizedString;
  /** Optional HTML label (admin-controlled). Takes precedence over label when set. */
  html?: CartLocalizedString;
}

/**
 * Full cart page (`#shop-cart-page`) content — shop-configurable, no market hardcode in widget.
 * Used when `page_layout === 'ukus'`.
 */
export interface CartPageSettings {
  notes_title?: CartLocalizedString;
  /** Note lines; HTML allowed (admin-controlled). */
  notes?: CartLocalizedString[];
  /** Default true. */
  show_coupon?: boolean;
  trust?: CartPageTrustItem[];
  pay_title?: CartLocalizedString;
  pay_lead?: CartLocalizedString;
  pay_items?: CartPagePayItem[];
  help_title?: CartLocalizedString;
  /** HTML body for Need help accordion (admin-controlled). */
  help_html?: CartLocalizedString;
  empty_title?: CartLocalizedString;
  empty_lead?: CartLocalizedString;
  /** Amount carved from shipping_cost as insurance. Default 7. */
  shipping_insurance?: number;
}

export interface CartWidgetSettings {
  /** Cart icon shape used across all modes */
  icon_type?: CartIconType;
  icon?: CartModeIconSettings;
  compact?: CartModeCompactSettings;
  summary?: CartModeSummarySettings;
  /**
   * Full cart page layout (`#shop-cart-page` only).
   * Drawer/modal cart always uses classic regardless of this flag.
   * `ukus` = item rows + notes|summary grid (+ optional strips from `page`).
   * Default / omitted = classic table layout.
   */
  page_layout?: 'classic' | 'ukus';
  /** Optional UKUS page chrome/content (trust / pay / help / notes). */
  page?: CartPageSettings;
}

/** Design tokens. primary/secondary/bg from palette; text/muted/border from defaults. */
export interface WidgetTheme {
  primary?: string;   // Main accent — buttons, links, header
  secondary?: string; // Secondary accent — badges, outlines
  header_links?: string; // Header slot links: auth/cart/currency/locale
  header_links_hover?: string;
  focus_visible_color?: string;
  focus_visible_width?: string;
  cart_badge_bg?: string;
  cart_badge_text?: string;
  surface_bg?: string;
  surface_border?: string;
  surface_shadow?: string;
  overlay_backdrop_color?: string;
  overlay_backdrop_blur?: string;
  heading_font?: string;
  heading_weight?: string;
  button_text_transform?: string;
  button_bg?: string;
  button_text?: string;
  button_border?: string;
  button_background_color?: string;
  button_text_color?: string;
  button_border_color?: string;
  button_hover_bg?: string;
  button_hover_text?: string;
  button_hover_border?: string;
  button_focus_bg?: string;
  button_focus_text?: string;
  button_active_bg?: string;
  button_active_text?: string;
  control_height?: string;
  spacing_scale?: string;
  motion_enabled?: string;
  motion_scale?: string;
  subscribe_modal_width?: string;
  z_index_base?: string;
  success?: string;
  danger?: string;
  warning?: string;
  bg?: string;       // Page background (palette.background)
  text?: string;
  muted?: string;
  border?: string;
  radius?: string;       // кнопки, инпуты
  radiusModal?: string;  // модалки, оверлеи
  font?: string;
  fontSize?: string;    // 1rem, 0.875rem, 1.125rem
  lineHeight?: string;   // 1.5, 1.4
  fontWeight?: string;  // 400, 500
  letterSpacing?: string; // normal, 0.02em
  fontSizeSm?: string;  // small/secondary: 0.875em
  font_size_sm?: string;
  shadow?: string;     // 0 0.5rem 1rem rgba(0,0,0,0.15)
  shadowSm?: string;   // 0 0.125rem 0.25rem rgba(0,0,0,0.075)
  shadowModal?: string; // modal/chat shadow
  transition?: string; // 0.15s ease-in-out
  focusRing?: string;  // 0.25 — opacity for focus ring
  linkDecoration?: string; // underline | none
  spacing?: string;    // 0.25rem — base spacing unit
  animationDuration?: string; // 0.3s — mount fade-in
  /** Card border: 'show' (default) or 'none' — applies to .sw-surface-card and layout static-page cards */
  card_border?: string;
  /** Card radius: 'show' (default) or 'none' — enable/disable rounded corners on content cards */
  card_radius?: string;
  /** Card shadow: 'show' (default) or 'none' — enable/disable box-shadow on content cards */
  card_shadow?: string;
  /** Palette id — if set and primary/secondary/bg missing, resolveTheme uses SHOPLIGHT_PALETTES */
  palette_id?: string;
}

/** 12 palettes from shoplight_palettes.php. Sync with backend. */
export const SHOPLIGHT_PALETTES: Record<string, { primary: string; secondary: string; background: string }> = {
  blue: { primary: '#3B82F6', secondary: '#60a5fa', background: '#FFFFFF' },
  'sky-blue': { primary: '#0ea5e9', secondary: '#38bdf8', background: '#FFFFFF' },
  'dark-blue': { primary: '#1e3a5f', secondary: '#3B82F6', background: '#FFFFFF' },
  navy: { primary: '#1e40af', secondary: '#60a5fa', background: '#FFFFFF' },
  green: { primary: '#1a7a4c', secondary: '#10B981', background: '#FFFFFF' },
  emerald: { primary: '#059669', secondary: '#34d399', background: '#FFFFFF' },
  teal: { primary: '#0d9488', secondary: '#2dd4bf', background: '#FFFFFF' },
  slate: { primary: '#475569', secondary: '#64748b', background: '#FFFFFF' },
  'classic-blue': { primary: '#2563eb', secondary: '#64748b', background: '#FFFFFF' },
  'dark-red': { primary: '#991b1b', secondary: '#dc2626', background: '#FFFFFF' },
  'light-red': { primary: '#ef4444', secondary: '#f87171', background: '#FFFFFF' },
  amber: { primary: '#d97706', secondary: '#f59e0b', background: '#FFFFFF' },
  'pharma-trust': { primary: '#1f6feb', secondary: '#5fa8ff', background: '#F8FBFF' },
  'pharma-clinical': { primary: '#0f766e', secondary: '#2dd4bf', background: '#F4FFFD' },
  'pharma-sterile': { primary: '#1e3a8a', secondary: '#60a5fa', background: '#F5F8FF' },
  'ed-discreet': { primary: '#1e293b', secondary: '#64748b', background: '#F8FAFC' },
  'ed-night': { primary: '#0b1f3a', secondary: '#365a8c', background: '#F4F7FB' },
  'ed-premium': { primary: '#1f3b5b', secondary: '#5b7694', background: '#F7F9FC' },
};

export type ThemePreset = 'default' | 'dark' | 'minimal' | 'pharma' | 'mens-health';
export type CartOpenMode = 'page' | 'modal' | 'side_drawer';

const THEME_PRESETS: Record<ThemePreset, WidgetTheme> = {
  default: {
    primary: '#0d6efd', secondary: '#6c757d', success: '#198754',
    danger: '#dc3545', warning: '#ffc107', bg: '#fff', text: '#212529',
    muted: '#6c757d', border: '#dee2e6', radius: '0.375rem',
  },
  dark: {
    primary: '#6ea8fe', secondary: '#a7acb1', success: '#75b798',
    danger: '#ea868f', warning: '#ffda6a', bg: '#212529', text: '#dee2e6',
    muted: '#adb5bd', border: '#495057', radius: '0.375rem',
  },
  minimal: {
    primary: '#111', secondary: '#666', success: '#2d6a4f',
    danger: '#c1121f', warning: '#e09f3e', bg: '#fff', text: '#333',
    muted: '#999', border: '#e0e0e0', radius: '0.25rem',
  },
  pharma: {
    primary: '#1a7a4c', secondary: '#5a8a72', success: '#0f6b3a',
    danger: '#c0392b', warning: '#e6a817', bg: '#f8fdf9', text: '#1a2e22',
    muted: '#6b8a7a', border: '#c8ddd0', radius: '0.5rem',
  },
  'mens-health': {
    primary: '#1b4f72', secondary: '#5d7b93', success: '#1e8449',
    danger: '#c0392b', warning: '#d4a017', bg: '#f4f6f8', text: '#1c2833',
    muted: '#7f8c8d', border: '#bdc3c7', radius: '0.375rem',
  },
};

export function resolveTheme(preset?: ThemePreset | string, overrides?: WidgetTheme): WidgetTheme {
  let base: WidgetTheme;
  const paletteId = overrides?.palette_id ?? (preset && SHOPLIGHT_PALETTES[preset] ? preset : undefined);
  if (paletteId && SHOPLIGHT_PALETTES[paletteId]) {
    const p = SHOPLIGHT_PALETTES[paletteId];
    base = {
      primary: p.primary, secondary: p.secondary, bg: p.background,
      success: '#198754', danger: '#dc3545', warning: '#ffc107',
      text: '#212529', muted: '#6c757d', border: '#dee2e6', radius: '0.375rem',
    };
  } else {
    base = THEME_PRESETS[(preset as ThemePreset)] ?? THEME_PRESETS.default;
  }
  if (!overrides) return base;
  const merged: WidgetTheme = { ...base };
  for (const [k, v] of Object.entries(overrides)) {
    if (k === 'palette_id') continue;
    if (v != null && v !== '') (merged as any)[k] = v;
  }
  return merged;
}

export interface LiveChatConfig {
  provider: string;    // 'phplive' | 'native' | 'deskpro' | ...
  enabled?: boolean;   // default true; admin can disable without removing config
  offline_action?: 'message' | 'ticket' | 'hide';
  // External providers (PHPLive, Deskpro, etc.) — injected by index.php
  script_url?: string;  // final URL (built by buildSupport from template + dept_id)
  live_chat_url?: string; // optional external chat page URL
  button_id?: string;
  launch_fn?: string;
  show_bubble?: boolean; // legacy/floating provider bubble
  show_bubble_mobile?: boolean;
  dept_id?: number;     // PHPLive department — controls theme; per-site
  // Native provider (spcadmin LiveChat) — handled by widget ChatBubble
  endpoint?: string;
  department_id?: number;
  poll_interval?: number;  // status poll ms, default 30000
}

export interface PrivateContactConfig {
  image_url?: string;
  action_url?: string;
}

export interface SupportConfig {
  email_contact?: PrivateContactConfig;
  phones?: Array<{ label?: string; contact?: PrivateContactConfig; number?: string; display?: string }>;
  working_hours?: string;
  livechat?: LiveChatConfig;
}

export interface WidgetConfig {
  siteKey: string;
  apiUrl: string;
  support?: SupportConfig;
  /** Базовый URL магазина для ссылок (аккаунт, заказы и т.д.). По умолчанию — origin из apiUrl. data-shop-base-url или __SHOP_WIDGET_CONFIG__.shopBaseUrl */
  shopBaseUrl?: string;
  /** Пресет темы или palette_id (blue, green, amber и т.д.). data-theme-preset / data-theme-palette */
  themePreset?: ThemePreset | string;
  /** Palette id — 12 палитр shoplight. Используется если theme не задан. */
  paletteId?: string;
  /** Overrides поверх пресета/палитры. data-theme-primary и т.д. или __SHOP_WIDGET_CONFIG__.theme */
  theme?: WidgetTheme;
  /** URL-маппинг view → страница хоста. Если задан для view — виджет делает redirect вместо модалки.
   *  Пример: { checkout: '/cart/checkout', 'checkout-qnr': '/cart/checkout/medical', 'checkout-confirm': '/cart/checkout/confirm', 'checkout-auth': '/cart/checkout/auth' }
   *  Также через data-атрибуты: data-url-checkout="/cart/checkout" data-url-checkout-qnr="/cart/medical" */
  viewUrls?: Partial<Record<string, string>>;
  /** Cart runtime behavior, independent from `viewUrls.cart`. */
  cartOpenMode?: CartOpenMode;
  /** Builder "Inline pages" placeholder ids (#shop-login-page, …). */
  inlinePlaceholders?: string[];
  /** Показывать ли блок выбора валюты (если есть placeholder #shop-currency-widget) */
  showCurrency?: boolean;
  /** Локализация. По умолчанию 'en'. data-locale или __SHOP_WIDGET_CONFIG__.locale */
  locale?: string;
  /** Allowed locale codes for language switcher (en,de,fr,pt,es,it). */
  availableLanguages?: string[];
  /** Tracking params injected from server */
  puid?: string;
  partner?: string;
  cmp?: string;
  cmpid?: string;
  sub1?: string;
  sub2?: string;
  sub3?: string;
  sub4?: string;
  sub5?: string;
  /** Preview token for approved='N' shops (admin/partner preview link) */
  previewToken?: string;
  /** Opaque token from shell /r/{token} boot — opens retention offer panel. */
  retentionOfferToken?: string | null;
  /** Chat bubble position: bottom-right (default), bottom-left, top-right, top-left */
  chatPosition?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** Chat window size: { width, height } in px */
  chatSize?: { width: number; height: number };
  /** Chat bubble z-index (default 20000000) */
  chatZIndex?: number;
  /** Modal max-width (e.g. 480px) */
  modalMaxWidth?: string;
  /** Close modal on backdrop click (default true) */
  closeOnBackdrop?: boolean;
  /** Default view when opening widget: cart | account | tickets */
  defaultView?: 'cart' | 'account' | 'tickets';
  /** Cart widget type (desktop): auto | icon | compact | summary */
  cartType?: 'auto' | 'icon' | 'compact' | 'summary';
  /** Per-mode appearance settings for cart widget */
  cartSettings?: CartWidgetSettings;
  /** Map of set name → country codes for geo check. { "European countries": ["DE","FR",...], "Canada": ["CA"] } */
  shippingCountriesSet?: Record<string, string[]>;
  /** Toggle visibility of personal discount in customer menu. */
  personalDiscountEnabled?: boolean;
  /** Toggle visibility of bonus account and bonus transactions in customer menu. */
  bonusAccountEnabled?: boolean;
  /** Toggle visibility of referral program in customer menu. */
  referralProgramEnabled?: boolean;
  /** Add-to-cart button texts. Per-locale: { en: { adding?, added? }, de: {...} }. Legacy: { adding?, added? } for en. */
  add_to_cart_texts?: { [locale: string]: { adding?: string; added?: string } } | { adding?: string; added?: string };
}

function normalizeSupport(support: unknown): SupportConfig | undefined {
  if (!support || typeof support !== 'object') return undefined;
  const src = support as Record<string, unknown>;

  let phones: SupportConfig['phones'] = undefined;
  const rawPhones = src.phones;
  if (Array.isArray(rawPhones)) {
    phones = rawPhones as SupportConfig['phones'];
  } else if (typeof rawPhones === 'string' && rawPhones.trim() !== '') {
    try {
      const parsed = JSON.parse(rawPhones);
      if (Array.isArray(parsed)) phones = parsed as SupportConfig['phones'];
    } catch {
      // ignore invalid legacy payload
    }
  }

  const livechat = (src.livechat && typeof src.livechat === 'object')
    ? normalizeLivechatConfig(src.livechat as Record<string, unknown>)
    : undefined;

  return {
    email_contact: (src.email_contact && typeof src.email_contact === 'object') ? src.email_contact as PrivateContactConfig : undefined,
    working_hours: typeof src.working_hours === 'string' ? src.working_hours : undefined,
    phones,
    livechat,
  };
}

function normalizeLivechatConfig(src: Record<string, unknown>): LiveChatConfig {
  const livechat = { ...src } as unknown as LiveChatConfig;
  const enabled = coerceOptionalBool(src.enabled);
  const showBubble = coerceOptionalBool(src.show_bubble);
  const showBubbleMobile = coerceOptionalBool(src.show_bubble_mobile);
  if (enabled !== undefined) livechat.enabled = enabled;
  if (showBubble !== undefined) livechat.show_bubble = showBubble;
  if (showBubbleMobile !== undefined) livechat.show_bubble_mobile = showBubbleMobile;
  return livechat;
}

declare global {
  interface Window {
    __SHOP_WIDGET_CONFIG__?: WidgetConfig;
  }
}

function coerceOptionalBool(value: unknown): boolean | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y', 'on'].includes(v)) return true;
    if (['0', 'false', 'no', 'n', 'off'].includes(v)) return false;
  }
  return undefined;
}

function normalizeCartOpenMode(value: unknown): CartOpenMode | undefined {
  if (value == null) return undefined;
  const raw = String(value).trim().toLowerCase();
  if (raw === 'page' || raw === 'modal' || raw === 'side_drawer') return raw;
  if (raw === 'drawer' || raw === 'drawler' || raw === 'side-drawer' || raw === 'side drawer') return 'side_drawer';
  return undefined;
}

function readCartOpenModeFromDocument(): CartOpenMode | undefined {
  if (typeof document === 'undefined') return undefined;
  const meta = document.querySelector('meta[name="sl-cart-open-mode"]');
  const fromMeta = normalizeCartOpenMode(meta?.getAttribute('content'));
  if (fromMeta) return fromMeta;
  return normalizeCartOpenMode(document.documentElement.getAttribute('data-sl-cart-open-mode'));
}

function readLocaleFromDocument(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const meta = document.querySelector('meta[name="sl-lang"]');
  const fromMeta = normalizeLocaleCode(meta?.getAttribute('content'));
  if (fromMeta) return fromMeta;
  const fromData = normalizeLocaleCode(document.documentElement.getAttribute('data-sl-lang'));
  if (fromData) return fromData;
  const fromHtml = normalizeLocaleCode(document.documentElement.lang);
  return fromHtml || undefined;
}

function normalizeAvailableLanguages(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const allow = new Set(['en', 'de', 'fr', 'pt', 'es', 'it']);
  const out = value
    .map((v) => String(v ?? '').trim().toLowerCase())
    .filter((v, i, arr) => v !== '' && allow.has(v) && arr.indexOf(v) === i);
  return out.length > 0 ? out : undefined;
}

function parseTheme(script: HTMLScriptElement | null): WidgetTheme | undefined {
  const win = typeof window !== 'undefined' ? window : null;
  const d = script?.dataset;
  const c = win?.__SHOP_WIDGET_CONFIG__?.theme;
  const pick = (attr: string | undefined, cfg: string | undefined) => attr ?? cfg ?? undefined;
  const t: WidgetTheme = {
    primary: pick(d?.themePrimary, c?.primary),
    secondary: pick(d?.themeSecondary, c?.secondary),
    success: pick(d?.themeSuccess, c?.success),
    danger: pick(d?.themeDanger, c?.danger),
    warning: pick(d?.themeWarning, c?.warning),
    bg: pick(d?.themeBg, c?.bg),
    text: pick(d?.themeText, c?.text),
    muted: pick(d?.themeMuted, c?.muted),
    border: pick(d?.themeBorder, c?.border),
    radius: pick(d?.themeRadius, c?.radius),
    radiusModal: pick(d?.themeRadiusModal, c?.radiusModal),
    button_bg: (c as any)?.button_bg ?? (c as any)?.button_background_color,
    button_text: (c as any)?.button_text ?? (c as any)?.button_text_color,
    button_border: (c as any)?.button_border ?? (c as any)?.button_border_color,
    font: pick(d?.themeFont, c?.font),
    fontSize: pick(d?.themeFontSize, c?.fontSize),
    lineHeight: pick(d?.themeLineHeight, c?.lineHeight),
    fontWeight: pick(d?.themeFontWeight, c?.fontWeight),
    letterSpacing: pick(d?.themeLetterSpacing, c?.letterSpacing),
    fontSizeSm: pick(d?.themeFontSizeSm, c?.fontSizeSm ?? c?.font_size_sm),
    shadow: pick(d?.themeShadow, c?.shadow),
    shadowSm: pick(d?.themeShadowSm, c?.shadowSm),
    shadowModal: pick(d?.themeShadowModal, c?.shadowModal),
    transition: pick(d?.themeTransition, c?.transition),
    focusRing: pick(d?.themeFocusRing, c?.focusRing),
    linkDecoration: pick(d?.themeLinkDecoration, c?.linkDecoration),
    spacing: pick(d?.themeSpacing, c?.spacing),
    animationDuration: pick(d?.themeAnimationDuration, c?.animationDuration),
    card_border: (d?.themeCardBorder ?? (c as any)?.card_border) ?? undefined,
    card_radius: (d?.themeCardRadius ?? (c as any)?.card_radius) ?? undefined,
    card_shadow: (d?.themeCardShadow ?? (c as any)?.card_shadow) ?? undefined,
  };
  if (Object.values(t).every((v) => v == null)) return undefined;
  return t;
}

function getWidgetScript(): HTMLScriptElement | null {
  const s = document.currentScript as HTMLScriptElement | null;
  if (s?.dataset?.siteKey != null) return s;
  return document.querySelector<HTMLScriptElement>('script[data-site-key][data-api-url]');
}

/** Parse data-url-* attributes → viewUrls map. data-url-checkout="/cart/checkout" → { checkout: '/cart/checkout' } */
function parseViewUrls(script: HTMLScriptElement | null): Record<string, string> | undefined {
  if (!script) return undefined;
  const map: Record<string, string> = {};
  const prefix = 'url';
  for (const [key, val] of Object.entries(script.dataset)) {
    if (!key.startsWith(prefix) || key.length <= prefix.length) continue;
    const rest = key.slice(prefix.length);
    const viewName = rest.charAt(0).toLowerCase() + rest.slice(1).replace(/([A-Z])/g, '-$1').toLowerCase();
    if (val?.trim()) map[viewName] = val.trim();
  }
  return Object.keys(map).length ? map : undefined;
}

/** True when running inside the builder preview-proxy (partner preview). */
export function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname.includes('/preview-proxy')) return true;
  const c = (window as any).__SHOP_WIDGET_CONFIG__;
  return !!(c?.previewToken);
}

function resolveShopBaseUrl(explicit: string | undefined, apiUrl: string): string | undefined {
  const trimmed = explicit?.trim();
  if (trimmed) return trimmed;
  if (typeof window !== 'undefined') return window.location.origin;
  if (apiUrl) {
    try {
      return new URL(apiUrl, 'https://localhost').origin;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export function getConfig(): WidgetConfig {
  const script = getWidgetScript();
  const scriptApiUrl = script?.dataset?.apiUrl ?? '';
  const scriptSiteKey = script?.dataset?.siteKey ?? '';

  if (typeof window !== 'undefined' && window.__SHOP_WIDGET_CONFIG__) {
    const c = window.__SHOP_WIDGET_CONFIG__;
    const apiUrl = (c.apiUrl?.trim() || scriptApiUrl) ?? '';
    const siteKey = (c.siteKey?.trim() || scriptSiteKey) ?? '';
    const shopBaseUrl = c.shopBaseUrl?.trim() || script?.dataset?.shopBaseUrl?.trim();
    const themePreset = (c.themePreset ?? script?.dataset?.themePreset) as ThemePreset | string | undefined;
    const paletteId = (c as any).paletteId ?? script?.dataset?.themePalette ?? (c.theme as any)?.palette_id;
    const rawTheme = c.theme as Record<string, unknown> | undefined;
    const theme = rawTheme ? { ...rawTheme, bg: (rawTheme.bg ?? rawTheme.background) as string } : undefined;
    return {
      siteKey,
      apiUrl,
      shopBaseUrl: resolveShopBaseUrl(shopBaseUrl, apiUrl),
      themePreset,
      paletteId: paletteId?.trim() || undefined,
      theme: theme ? { ...theme, palette_id: (theme as any).palette_id ?? paletteId } : undefined,
      viewUrls: c.viewUrls ?? (c as any).view_urls ?? parseViewUrls(script),
      cartOpenMode: normalizeCartOpenMode(c.cartOpenMode ?? (c as any).cart_open_mode ?? script?.dataset?.cartOpenMode ?? readCartOpenModeFromDocument()),
      inlinePlaceholders: Array.isArray(c.inlinePlaceholders)
        ? c.inlinePlaceholders
        : (Array.isArray((c as any).inline_placeholders) ? (c as any).inline_placeholders : undefined),
      showCurrency: c.showCurrency,
      support: normalizeSupport(c.support),
      puid: c.puid ?? (c as { tracking?: { puid?: string } }).tracking?.puid,
      partner: c.partner ?? (c as { tracking?: { partner?: string } }).tracking?.partner,
      cmp: c.cmp ?? (c as { tracking?: { cmp?: string } }).tracking?.cmp,
      cmpid: (c.cmpid ?? (c as { tracking?: { cmpid?: string | number } }).tracking?.cmpid) as string | undefined,
      sub1: (c as any).sub1,
      sub2: (c as any).sub2,
      sub3: (c as any).sub3,
      sub4: (c as any).sub4,
      sub5: (c as any).sub5,
      previewToken: c.previewToken,
      retentionOfferToken: (c as any).retentionOfferToken ?? (c as any).retention_offer_token ?? null,
      chatPosition: c.chatPosition,
      chatSize: c.chatSize,
      chatZIndex: c.chatZIndex,
      modalMaxWidth: c.modalMaxWidth,
      closeOnBackdrop: c.closeOnBackdrop,
      defaultView: c.defaultView,
      cartType: c.cartType,
      cartSettings: c.cartSettings ?? (c as any).cart_settings,
      availableLanguages: normalizeAvailableLanguages((c as any).availableLanguages ?? (c as any).available_languages),
      shippingCountriesSet: (c as any).shipping_countries_set ?? (c as any).shippingCountriesSet,
      add_to_cart_texts: (c as any).add_to_cart_texts,
      personalDiscountEnabled: coerceOptionalBool((c as any).personal_discount_enabled ?? (c as any).personalDiscountEnabled),
      bonusAccountEnabled: coerceOptionalBool((c as any).bonus_account_enabled ?? (c as any).bonusAccountEnabled),
      referralProgramEnabled: coerceOptionalBool((c as any).referral_program_enabled ?? (c as any).referralProgramEnabled),
      locale: (c.locale ?? script?.dataset?.locale ?? readLocaleFromDocument())?.trim() || undefined,
    };
  }
  const theme = parseTheme(script);
  const themePreset = script?.dataset?.themePreset as ThemePreset | undefined;
  const paletteId = script?.dataset?.themePalette?.trim();
  const showCurrency = script?.dataset?.showCurrency !== undefined
    ? script.dataset.showCurrency !== 'false' && script.dataset.showCurrency !== '0'
    : undefined;
  const apiUrl = scriptApiUrl ?? '';
  const shopBaseUrl = resolveShopBaseUrl(script?.dataset?.shopBaseUrl?.trim(), apiUrl);
  const viewUrls = parseViewUrls(script);
  const cartOpenMode = normalizeCartOpenMode(script?.dataset?.cartOpenMode ?? readCartOpenModeFromDocument());
  const themeWithPalette = theme || paletteId
    ? { ...theme, palette_id: paletteId ?? (theme as any)?.palette_id }
    : undefined;
  const cartType = script?.dataset?.cartType as 'auto' | 'icon' | 'compact' | 'summary' | undefined;
  const cartSettings = script?.dataset?.cartSettings ? JSON.parse(script.dataset.cartSettings) : undefined;
  const availableLanguages = script?.dataset?.availableLanguages
    ? normalizeAvailableLanguages(script.dataset.availableLanguages.split(','))
    : undefined;
  return {
    siteKey: scriptSiteKey,
    apiUrl,
    shopBaseUrl,
    themePreset,
    paletteId,
    theme: themeWithPalette,
    viewUrls,
    cartOpenMode,
    showCurrency,
    cartType,
    cartSettings,
    availableLanguages,
    personalDiscountEnabled: coerceOptionalBool(script?.dataset?.personalDiscountEnabled),
    bonusAccountEnabled: coerceOptionalBool(script?.dataset?.bonusAccountEnabled),
    referralProgramEnabled: coerceOptionalBool(script?.dataset?.referralProgramEnabled),
    locale: script?.dataset?.locale?.trim() || readLocaleFromDocument() || undefined,
  };
}
